import { useEffect, lazy, Suspense } from "react";
import Header from "@/components/sections/Header";
import Hero from "@/components/sections/Hero";
import Challenges from "@/components/sections/Challenges";
import Audience from "@/components/sections/Audience";
import Leistungen from "@/components/sections/Leistungen";
import Results from "@/components/sections/Results";
import Team from "@/components/sections/Team";
import CalculatorCTA from "@/components/sections/CalculatorCTA";
import CookieBanner from "@/components/CookieBanner";
import SEO from "@/components/SEO";

// Below-the-fold sections werden lazy geladen, halbiert das initiale
// JS-Bundle und macht LCP + FCP deutlich schneller. Framer-Motion und
// die schweren Marquees / Timelines laden erst wenn der User scrollt.
const Showcase = lazy(() => import("@/components/sections/Showcase"));
const ServiceDetails = lazy(() => import("@/components/sections/ServiceDetails"));
const Reviews = lazy(() => import("@/components/sections/Reviews"));
const Process = lazy(() => import("@/components/sections/Process"));
const Contact = lazy(() => import("@/components/sections/Contact"));
const FAQ = lazy(() => import("@/components/sections/FAQ"));
const Footer = lazy(() => import("@/components/sections/Footer"));

// Platzhalter mit reservierter Höhe verhindert Layout-Shift, während der
// Chunk aus dem Netz kommt.
const SectionSkeleton = ({ minH = "60vh" }) => (
  <div aria-hidden style={{ minHeight: minH }} className="bg-ink" />
);

export default function Home() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Prefetch below-fold section chunks während der Browser idle ist .
  // sorgt für nahtloses Scrollen ohne "Lädt…" Blitzer, ohne die initiale
  // LCP zu belasten.
  useEffect(() => {
    const prefetch = () => {
      import("@/components/sections/Showcase");
      import("@/components/sections/ServiceDetails");
      import("@/components/sections/Reviews");
      import("@/components/sections/Process");
      import("@/components/sections/Contact");
      import("@/components/sections/FAQ");
      import("@/components/sections/Footer");
    };
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 2500 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(prefetch, 1500);
    return () => clearTimeout(t);
  }, []);

  // URL Parameter Weitergabe an Rechner Links.
  // Wenn User via Google Ads mit ?gclid=... oder utm_* hier landet, sollen
  // diese Parameter auf jeden Link zu cre8-studio-rechner.de weitergereicht
  // werden, sonst geht Attribution zwischen Landing und Rechner verloren.
  // Bestehende Parameter am Ziel werden nicht überschrieben.
  useEffect(() => {
    const search = window.location.search;
    if (!search) return;
    const currentParams = new URLSearchParams(search);
    if (![...currentParams].length) return;

    const decorate = (link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (!/cre8-studio-rechner\.de/i.test(url.hostname)) return;
        let changed = false;
        currentParams.forEach((value, key) => {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
            changed = true;
          }
        });
        if (changed) link.href = url.toString();
      } catch {
        /* invalid URL, ignore */
      }
    };

    const process = () => {
      document
        .querySelectorAll('a[href*="cre8-studio-rechner.de"]')
        .forEach(decorate);
    };

    process();
    // Da mehrere Sections lazy geladen werden, beobachten wir das DOM
    // und rewriten neu gemountete Links passend nach.
    const observer = new MutationObserver(process);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (

    <main id="main" data-testid="home-page" className="relative">
      <SEO
        title=""
        description="CRE8 Studio GmbH. Medienagentur, die Unternehmen dabei unterstützt, mit klaren medialen Inhalten ihre Zielgruppe zu erreichen und Neukunden zu gewinnen. Social Media Content, Werbevideos, Werbeanzeigen."
        path="/"
      />
      <a
        href="#main"
        data-testid="skip-link"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:bg-signal focus:text-bone focus:px-4 focus:py-2 focus:rounded-full focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Zum Hauptinhalt springen
      </a>
      <Header />
      <Hero />
      <Challenges />
      <Audience />
      <Leistungen />
      <Results />
      <Suspense fallback={<SectionSkeleton minH="90vh" />}>
        <Contact />
      </Suspense>
      <Team />
      <Suspense fallback={<SectionSkeleton minH="100vh" />}>
        <Process />
      </Suspense>
      <Suspense fallback={<SectionSkeleton minH="80vh" />}>
        <Showcase />
      </Suspense>
      <Suspense fallback={<SectionSkeleton minH="100vh" />}>
        <Reviews />
      </Suspense>
      <Suspense fallback={<SectionSkeleton minH="80vh" />}>
        <ServiceDetails />
      </Suspense>
      <CalculatorCTA />
      <Suspense fallback={<SectionSkeleton minH="80vh" />}>
        <FAQ />
      </Suspense>
      <Suspense fallback={<SectionSkeleton minH="40vh" />}>
        <Footer />
      </Suspense>
      <CookieBanner />
    </main>
  );
}
